import { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { StarRating } from "./StarRating";

export type LightboxPhoto = {
  url: string;
  rating: number;
  name: string;
  comment?: string | null;
  title?: string | null;
};

export function ReviewPhotoLightbox({
  photos,
  index,
  onClose,
  onIndexChange,
}: {
  photos: LightboxPhoto[];
  index: number;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}) {
  const total = photos.length;
  const go = useCallback(
    (delta: number) => onIndexChange((index + delta + total) % total),
    [index, total, onIndexChange],
  );

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  const photo = photos[index];
  if (!photo) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Customer review photo"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        aria-label="Close photo"
        className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-[#2C2C2C]"
      >
        <X size={18} />
      </button>

      {total > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              go(-1);
            }}
            aria-label="Previous photo"
            className="absolute left-3 rounded-full bg-white/90 p-2 text-[#2C2C2C] md:left-6"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              go(1);
            }}
            aria-label="Next photo"
            className="absolute right-3 rounded-full bg-white/90 p-2 text-[#2C2C2C] md:right-6"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      <div
        className="grid max-h-[86vh] w-full max-w-4xl gap-0 overflow-hidden rounded-2xl bg-white md:grid-cols-[1.4fr,1fr]"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={photo.url}
          alt={`Photo from a ${photo.rating}-star review by ${photo.name}`}
          className="max-h-[50vh] w-full bg-black object-contain md:max-h-[86vh]"
        />
        <div className="max-h-[36vh] overflow-y-auto p-5 md:max-h-[86vh] md:p-6">
          <StarRating value={photo.rating} />
          <p className="mt-2 font-medium text-[#2C2C2C]">{photo.name}</p>
          {photo.title && <p className="mt-2 font-medium text-[#2C2C2C]">{photo.title}</p>}
          {photo.comment && (
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-[#2C2C2C]/80">
              {photo.comment}
            </p>
          )}
          {total > 1 && (
            <p className="mt-4 text-xs text-[#2C2C2C]/50">
              Photo {index + 1} of {total}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
